import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Trophy, Medal, Flame, Star, Target, TrendingUp, Users, Building2,
  Award, Clock, CheckCircle, Play, Calendar, ChevronRight
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGES = {
  first_session: { 
    name: 'First Session', 
    icon: '🎯', 
    description: 'Completed your first session',
    color: '#10B981'
  },
  five_day_streak: { 
    name: '5-Day Streak', 
    icon: '🔥', 
    description: 'Logged activity 5 days in a row',
    color: '#F59E0B'
  }
};

const Leaderboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('global');
  const [leaderboard, setLeaderboard] = useState([]);
  const [userStats, setUserStats] = useState({
    points: 0,
    rank: 0,
    streak: 0,
    badges: [],
    activity_summary: {
      sessions_completed: 0,
      daily_scans_submitted: 0,
      tasks_completed: 0,
      total_time_hours: 0
    }
  });
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, leaderboardRes, habitsRes] = await Promise.all([
        axios.get(`${API_URL}/gamification/my-stats`, { headers }),
        axios.get(`${API_URL}/gamification/leaderboard?scope=${activeTab}`, { headers }),
        axios.get(`${API_URL}/gamification/habits`, { headers })
      ]);

      setUserStats(statsRes.data);
      setLeaderboard(leaderboardRes.data);
      setHabits(habitsRes.data);
    } catch (error) {
      console.error('Failed to fetch gamification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy size={20} style={{ color: '#F59E0B' }} />;
    if (rank === 2) return <Medal size={20} style={{ color: '#94A3B8' }} />;
    if (rank === 3) return <Medal size={20} style={{ color: '#CD7F32' }} />;
    return <span style={{ fontWeight: '700', color: 'var(--text-tertiary)' }}>#{rank}</span>;
  };

  const formatPoints = (points) => {
    if (points >= 1000) return `${(points / 1000).toFixed(1)}k`;
    return points;
  };

  if (loading) {
    return (
      <div className="content-area" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="content-area">
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
          Activity & Rewards
        </h1>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
          Track your progress and compete with colleagues
        </p>
      </div>

      {/* User Stats Card */}
      <div className="card" style={{ 
        padding: '24px', 
        marginBottom: '24px',
        background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
        color: 'white'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ 
              width: '80px', height: '80px', borderRadius: '50%', 
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '36px', fontWeight: '700'
            }}>
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <div>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '24px', fontWeight: '700' }}>
                {user?.full_name || 'User'}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.9 }}>
                <Trophy size={16} />
                <span>Rank #{userStats.rank || '-'} {activeTab === 'global' ? 'globally' : 'in department'}</span>
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: '700' }}>{formatPoints(userStats.points)}</div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>Total Points</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                <Flame size={24} style={{ color: '#F59E0B' }} />
                {userStats.streak}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>Day Streak</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: '700' }}>{userStats.badges?.length || 0}</div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>Badges</div>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Summary */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
        gap: '16px', 
        marginBottom: '24px' 
      }}>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <Play size={24} style={{ color: '#6366F1', marginBottom: '8px' }} />
          <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>
            {userStats.activity_summary?.sessions_completed || 0}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Sessions Completed</div>
        </div>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <Calendar size={24} style={{ color: '#10B981', marginBottom: '8px' }} />
          <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>
            {userStats.activity_summary?.daily_scans_submitted || 0}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Daily Scans</div>
        </div>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <CheckCircle size={24} style={{ color: '#F59E0B', marginBottom: '8px' }} />
          <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>
            {userStats.activity_summary?.tasks_completed || 0}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Tasks Completed</div>
        </div>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <Clock size={24} style={{ color: '#8B5CF6', marginBottom: '8px' }} />
          <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>
            {(userStats.activity_summary?.total_time_hours || 0).toFixed(1)}h
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Total Time</div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Leaderboard */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Leaderboard
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setActiveTab('global')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  background: activeTab === 'global' ? '#6366F1' : 'var(--bg-secondary)',
                  color: activeTab === 'global' ? 'white' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Users size={14} />
                Global
              </button>
              <button
                onClick={() => setActiveTab('department')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  background: activeTab === 'department' ? '#6366F1' : 'var(--bg-secondary)',
                  color: activeTab === 'department' ? 'white' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Building2 size={14} />
                Department
              </button>
            </div>
          </div>

          {leaderboard.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-tertiary)' }}>
              <Trophy size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p style={{ margin: 0 }}>No leaderboard data yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {leaderboard.slice(0, 10).map((entry, idx) => (
                <div 
                  key={idx} 
                  style={{ 
                    padding: '12px 16px', 
                    background: entry.user_id === user?.id ? 'rgba(99, 102, 241, 0.1)' : 'var(--bg-secondary)', 
                    borderRadius: '8px',
                    border: entry.user_id === user?.id ? '2px solid #6366F1' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '32px', textAlign: 'center' }}>
                      {getRankIcon(idx + 1)}
                    </div>
                    <div style={{ 
                      width: '36px', height: '36px', borderRadius: '50%', 
                      background: '#6366F1',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: '600', fontSize: '14px'
                    }}>
                      {entry.name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '14px' }}>
                        {entry.name}
                        {entry.user_id === user?.id && <span style={{ color: '#6366F1', marginLeft: '8px' }}>(You)</span>}
                      </div>
                      {entry.department && (
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{entry.department}</div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Star size={14} style={{ color: '#F59E0B' }} />
                    <span style={{ fontWeight: '700', color: 'var(--text-primary)' }}>
                      {formatPoints(entry.points)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Badges & Habits */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Badges */}
          <div className="card" style={{ padding: '24px' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Your Badges
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {Object.entries(BADGES).map(([key, badge]) => {
                const earned = userStats.badges?.includes(key);
                return (
                  <div 
                    key={key}
                    style={{
                      padding: '16px',
                      borderRadius: '12px',
                      background: earned ? `${badge.color}15` : 'var(--bg-secondary)',
                      border: earned ? `2px solid ${badge.color}` : '2px dashed var(--border-color)',
                      textAlign: 'center',
                      minWidth: '120px',
                      opacity: earned ? 1 : 0.5
                    }}
                  >
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>{badge.icon}</div>
                    <div style={{ 
                      fontWeight: '600', 
                      fontSize: '13px', 
                      color: earned ? badge.color : 'var(--text-tertiary)' 
                    }}>
                      {badge.name}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                      {earned ? 'Earned!' : badge.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Habit Tracker */}
          <div className="card" style={{ padding: '24px' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Weekly Activity
            </h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                const habitData = habits[idx] || { active: false };
                return (
                  <div key={day} style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ 
                      width: '40px', height: '40px', 
                      borderRadius: '50%', 
                      margin: '0 auto 8px',
                      background: habitData.active ? '#10B981' : 'var(--bg-secondary)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      {habitData.active ? (
                        <CheckCircle size={20} style={{ color: 'white' }} />
                      ) : (
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--border-color)' }} />
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: '500' }}>{day}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ 
              marginTop: '20px', 
              padding: '12px', 
              background: 'var(--bg-secondary)', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Flame size={18} style={{ color: '#F59E0B' }} />
                <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                  {userStats.streak} day streak
                </span>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                {userStats.streak >= 5 ? 'Amazing! Keep it up!' : `${5 - userStats.streak} more days to earn your streak badge!`}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
